import { eventIterator, oc } from "@orpc/contract";
import { z } from "zod";
import { captureGrantSchema, screenListSchema } from "./capture";
import { catalogErrors } from "./errors";
import { idSchema } from "./ids";
import { recordingPackageSchema } from "./recording-package";
import {
  addNoteInputSchema,
  correctAnswerInputSchema,
  createRecordingInputSchema,
  removePartInputSchema,
  recordingListQuerySchema,
  recordingListSchema,
  recordingTapeFrameSchema,
  recordingTapeSchema,
  sendEventInputSchema,
  sentEventSchema,
  tapeQuerySchema,
} from "./recordings";
import { voiceJoinQuerySchema, voiceJoinSchema } from "./voice";

/**
 * The regional Recordings API: start a recording on a chat, read its tape, and send what the
 * person does on the island, and follow its tape as it grows. The server mounts this under `/api`.
 * Voice, capture, timers and judgments write to the same tape from inside the API, never through
 * these routes: the desktop app adds screens through the Capture API (`./capture/contract`) with a
 * grant the island asks for here.
 *
 * Inputs are "detailed" so path parameters, the query string and the body stay apart. A malformed
 * ID names something that cannot exist, so the server answers an issue under `params` with
 * NOT_FOUND.
 */
const workspace = z.object({ workspaceId: z.string().min(1) });
const inWorkspace = z.object({ params: workspace });
const onRecording = z.object({
  params: workspace.extend({ recordingId: idSchema("recording") }),
});

const route = oc.$route({ inputStructure: "detailed" });

export const recordingsContract = oc
  .errors(
    catalogErrors([
      "UNAUTHORIZED",
      "FORBIDDEN",
      "NOT_FOUND",
      "VALIDATION_ERROR",
      "INTERNAL_ERROR",
      "SERVICE_UNAVAILABLE",
    ]),
  )
  .tag("Recordings")
  .prefix("/workspaces/{workspaceId}/recordings")
  .router({
    /** The chat's owner starts one; the first page of its tape comes back. */
    create: route
      .route({ method: "POST", path: "/", successStatus: 201 })
      .errors(catalogErrors(["RECORDING_IN_PROGRESS"]))
      .input(inWorkspace.extend({ body: createRecordingInputSchema }))
      .output(recordingTapeSchema),
    /** Anyone who may open the chat lists its recordings. */
    list: route
      .route({ method: "GET", path: "/" })
      .input(inWorkspace.extend({ query: recordingListQuerySchema }))
      .output(recordingListSchema),
    /** Anyone who may open the chat reads its recordings; `after` is the last position the reader has. */
    tape: route
      .route({ method: "GET", path: "/{recordingId}" })
      .input(onRecording.extend({ query: tapeQuerySchema }))
      .output(recordingTapeSchema),
    /** The chat's owner sends what they did on the island. */
    send: route
      .route({ method: "POST", path: "/{recordingId}/events" })
      .errors(catalogErrors(["RECORDING_ENDED", "RECORDING_IN_PROGRESS"]))
      .input(onRecording.extend({ body: sendEventInputSchema }))
      .output(sentEventSchema),
    /**
     * The chat's owner deletes an ended recording, with its tape. A live or paused one is ended
     * from its island first.
     */
    remove: route
      .route({ method: "DELETE", path: "/{recordingId}", successStatus: 204 })
      .errors(catalogErrors(["RECORDING_NOT_ENDED"]))
      .input(onRecording),
    /**
     * The chat's owner asks for a grant that lets the desktop app add screens to this recording
     * only. The island asks again before `expiresAt`; an ended recording issues none.
     */
    grant: route
      .route({ method: "POST", path: "/{recordingId}/capture-grant" })
      .errors(catalogErrors(["RECORDING_ENDED"]))
      .input(onRecording)
      .output(captureGrantSchema),
    /**
     * The chat's owner asks to join a recording's voice room. The API makes sure the voice worker
     * is there (starting it with its own grant) and answers LiveKit's address and a token for this
     * person in this room. A recording that asks in text only, or ended, answers none.
     */
    voiceJoin: route
      .route({ method: "POST", path: "/{recordingId}/voice-join" })
      .errors(catalogErrors(["RECORDING_ENDED"]))
      .input(onRecording.extend({ query: voiceJoinQuerySchema }))
      .output(voiceJoinSchema),
    /** Anyone who may open the chat lists the screens kept during a recording, oldest first. */
    screens: route
      .route({ method: "GET", path: "/{recordingId}/screens" })
      .input(onRecording)
      .output(screenListSchema),
    /**
     * Anyone who may open the chat reads a recording's package (ENG-1028): its tape, screens and
     * trace laid out on one clock. Its screens' images are read through the files API.
     */
    package: route
      .route({ method: "GET", path: "/{recordingId}/package" })
      .input(onRecording)
      .output(recordingPackageSchema),
    /**
     * After a recording ended (ENG-1029), the chat's owner who made it adds a note at a moment,
     * corrects an answer, or removes a part or a stretch of time. Nothing captured is edited: each
     * is added to the tape, and the answer is the package as it now folds. A going recording is
     * RECORDING_NOT_ENDED; a question, screen, action or line it does not have is NOT_FOUND; a
     * moment off its clock, or a part already removed, is VALIDATION_ERROR. A correction made
     * against an answer that no longer stands, or a key sent again with another change, is
     * CONFLICT. When the draft could not be told, it is SERVICE_UNAVAILABLE: the change is kept,
     * and sending it again with the same key tells the draft.
     */
    addNote: route
      .route({ method: "POST", path: "/{recordingId}/notes" })
      .errors(catalogErrors(["RECORDING_NOT_ENDED", "CONFLICT"]))
      .input(onRecording.extend({ body: addNoteInputSchema }))
      .output(recordingPackageSchema),
    correctAnswer: route
      .route({ method: "POST", path: "/{recordingId}/corrections" })
      .errors(catalogErrors(["RECORDING_NOT_ENDED", "CONFLICT"]))
      .input(onRecording.extend({ body: correctAnswerInputSchema }))
      .output(recordingPackageSchema),
    removePart: route
      .route({ method: "POST", path: "/{recordingId}/removals" })
      .errors(catalogErrors(["RECORDING_NOT_ENDED", "CONFLICT"]))
      .input(onRecording.extend({ body: removePartInputSchema }))
      .output(recordingPackageSchema),
    /**
     * Anyone who may open the chat follows a recording: server-sent frames, the snapshot after
     * `after` and then each entry as it lands. The server ends a stream within a minute; the reader
     * reconnects from the last position it has, or catches up with `tape`.
     */
    stream: route
      .route({ method: "GET", path: "/{recordingId}/stream" })
      .input(onRecording.extend({ query: tapeQuerySchema }))
      .output(eventIterator(recordingTapeFrameSchema)),
  });

export type RecordingsContract = typeof recordingsContract;
